import { Select } from "@suraasa/placebo-ui"
import { useQuery } from "@tanstack/react-query"
import api from "api"
import Navbar from "components/Navbar"
import PageContainer from "components/PageContainer"
import { useNavigate } from "react-router-dom"
import { AuthManager } from "utils/AuthManager"
import { userEmailsForChecks } from "utils/constants"
import useSearchField from "utils/hooks/useSearchField"
import { useSearchParamsHook } from "utils/hooks/useSearchParams"
import { useProductFilterOptions } from "utils/products"
import { routes } from "utils/routes"

import AssignmentsList from "./AssignmentsList"
import OverviewStatCard from "./OverviewStatCard"
import StudentsList from "./StudentsList"

export enum Type {
  student = "student",
  assignment = "assignment",
}

const viewModeOptions = [
  {
    label: "By Student",
    value: Type.student,
  },
  {
    label: "By Assignment",
    value: Type.assignment,
  },
]

type Props = {
  type: Type
}

const ListStudentsOrAssignments = ({ type }: Props) => {
  const navigate = useNavigate()
  const isByAssignment = type === Type.assignment

  const { searchParams, updateParam } = useSearchParamsHook({
    course: "",
    product: "",
  })

  const {
    SearchField,
    searchText,
    reset: resetSearch,
  } = useSearchField({
    className: "-mt-2",
    placeholder: isByAssignment
      ? "Search by assignment title"
      : "Search by student name or email",
    useSearchStyles: false,
    useSearchParam: true,
  })

  const courses = useQuery({
    queryFn: () => api.courses.list(),
    queryKey: ["courses"],
    select: data => {
      const allowedModules = [
        "Advanced Questioning Skills and Techniques",
        "Advanced Instructional Strategies",
        "Authentic Assessment and Measuring Student Outcomes",
        "Technology and the Science of Learning",
        "Emerging Issues in Learning Taxonomies",
        "Advanced Curriculum Design",
        "Advanced Instructional Planning for Learning Needs",
        "English Language for Multilingual Classrooms",
        "Teaching English Language and Literacy (FC)",
        "Teaching Mathematics and Numeracy (FC)",
        "Teaching Specialist Subject (FC)",
        "Teaching in Early Years (FC)",
        "Action Research for School Improvement",
      ].map(module => module.toLowerCase().trim())

      const email = AuthManager.getAuthInfo()?.user.email
      if (email === userEmailsForChecks.monica) {
        return data.courses.filter(course =>
          allowedModules.includes(course.name.toLowerCase().trim())
        )
      }

      return data.courses
    },
  })

  const productFilterOptions = useProductFilterOptions()

  const selectedProduct = productFilterOptions.find(
    c => c.label === searchParams.product
  )

  const selectedCourse = courses.data?.find(c => c.uuid === searchParams.course)

  const overview = useQuery({
    queryFn: () =>
      api.assignments.stats({
        data: {
          ...(selectedProduct && {
            product_filter: {
              parent_item_id: selectedProduct?.value.id || null,
              parent_item_type: selectedProduct?.value.type || null,
              delivery_modes: selectedProduct?.value.deliveryModes || null,
            },
          }),
          ...(selectedCourse && {
            course_ids: [selectedCourse.uuid],
          }),
        },
      }),
    queryKey: [
      "assignment-stats",
      selectedProduct?.value,
      selectedCourse?.uuid,
    ],
  })

  return (
    <>
      <Navbar hideBackButton />
      <PageContainer>
        <div className="flex flex-col gap-1">
          {SearchField}

          <div className="flex gap-1">
            <Select
              options={productFilterOptions}
              placeholder="Filter by product"
              onChange={v => updateParam("product", v?.label)}
              value={selectedProduct}
              isClearable
            />

            <Select
              options={courses.data}
              placeholder="Filter by course"
              onChange={v => updateParam("course", v?.uuid)}
              value={selectedCourse}
              isLoading={courses.isLoading}
              isClearable
              getOptionLabel={option => option.name}
              getOptionValue={option => option.uuid}
            />

            <Select
              options={viewModeOptions}
              onChange={option => {
                if (!option) return

                resetSearch()

                if (option.value === Type.assignment) {
                  navigate(routes.assignment.list, { replace: true })
                }
                if (option.value === Type.student) {
                  navigate(routes.students.list, { replace: true })
                }
              }}
              value={viewModeOptions.find(v => v.value === type)}
              className="max-w-xs"
            />
          </div>
        </div>

        <div className="mt-2 flex gap-1">
          <OverviewStatCard
            color="bg-warning-300"
            count={overview.data?.totalSubmissions}
            title="Total Submissions"
          />
          <OverviewStatCard
            color="bg-success-300"
            count={overview.data?.acceptedSubmissions}
            title="Accepted Submissions"
          />
          <OverviewStatCard
            color="bg-critical-300"
            count={overview.data?.pendingReviews}
            title="Pending Reviews"
          />
        </div>

        {isByAssignment ? (
          <AssignmentsList
            courseId={selectedCourse?.uuid}
            product={selectedProduct?.value}
            search={searchText}
          />
        ) : (
          <StudentsList
            courseId={selectedCourse?.uuid}
            search={searchText}
            product={selectedProduct?.value}
          />
        )}
      </PageContainer>
    </>
  )
}

export default ListStudentsOrAssignments
